import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Eye, EyeOff, ImagePlus, Loader2, History, RotateCcw } from "lucide-react";
import { api, formatApiError, resolveImageUrl } from "../../lib/api";

const SECTION_LABELS = {
  hero: "Topo (chamada principal)",
  marquee: "Faixa animada",
  catalog: "Catálogo de produtos",
  atmosphere: "Destaque escuro (Como Comprar)",
  services: "Serviços (assistência, loja, entrega)",
  simulators: "Simuladores (parcelas e frete)",
  reviews: "Avaliações de clientes",
  contact: "Fale com a Loja (formulário)",
};

function Field({ label, value, onChange, multiline, testid, placeholder }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-1.5">{label}</label>
      {multiline ? (
        <textarea rows={2} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
          className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors resize-none" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
          className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors" />
      )}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange(data.url);
      toast.success("Imagem enviada!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-1.5">{label}</label>
      <div className="flex items-center gap-3">
        {value && <img src={resolveImageUrl(value)} alt={label} className="w-14 h-14 rounded-xl object-cover border border-[#E8E2D9]" />}
        <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" />
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading} data-testid={testid}
          className="inline-flex items-center gap-2 border border-[#D5CDC2] px-4 py-2 rounded-full font-display text-xs font-medium hover:border-[#1A1816] transition-colors disabled:opacity-60">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {value ? "Trocar imagem" : "Enviar imagem"}
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="text-xs text-[#6B645C] hover:text-red-600 transition-colors">
            Remover
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminSite() {
  const [content, setContent] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/content").then((res) => setContent(res.data)).catch(() => {});
    api.get("/content/history").then((res) => setHistory(res.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  if (!content) return <p className="text-sm text-[#6B645C]">Carregando...</p>;

  const set = (path, value) => {
    setContent((c) => {
      const clone = JSON.parse(JSON.stringify(c));
      const keys = path.split(".");
      let obj = clone;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return clone;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Site atualizado! As alterações já estão no ar.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (id) => {
    if (!window.confirm("Restaurar essa versão anterior do conteúdo?")) return;
    try {
      await api.post(`/content/restore/${id}`);
      toast.success("Versão restaurada!");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const moveSection = (idx, dir) => {
    const sections = [...content.sections];
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    [sections[idx], sections[target]] = [sections[target], sections[idx]];
    set("sections", sections);
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="admin-site">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-2xl tracking-tight">Conteúdo do Site</h1>
        <div className="flex gap-2">
          <button type="button" data-testid="site-preview-button"
            onClick={() => {
              localStorage.setItem("draft_content", JSON.stringify(content));
              window.open("/?preview=1", "_blank");
            }}
            className="inline-flex items-center gap-2 border border-[#D5CDC2] px-5 py-3 rounded-full font-display text-xs uppercase tracking-wider font-semibold hover:border-[#1A1816] transition-colors">
            <Eye className="w-4 h-4" /> Visualizar
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="site-save-button"
            className="inline-flex items-center gap-2 bg-[var(--brand)] text-white px-6 py-3 rounded-full font-display text-xs uppercase tracking-wider font-semibold hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Salvando..." : "Publicar Alterações"}
          </button>
        </div>
      </div>

      {/* Aparência */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Aparência</h2>
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-2">Cor de destaque do site</label>
          <div className="flex flex-wrap gap-2 items-center" data-testid="accent-swatches">
            {["#E2725B", "#C2185B", "#2563EB", "#059669", "#7C3AED", "#D97706", "#DC2626", "#0F766E"].map((c) => (
              <button key={c} type="button" onClick={() => set("theme.accent", c)} data-testid={`swatch-${c}`}
                className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${content.theme?.accent === c ? "border-[#1A1816] scale-110" : "border-white shadow"}`}
                style={{ backgroundColor: c }} aria-label={`Cor ${c}`} />
            ))}
            <input value={content.theme?.accent || "#E2725B"} onChange={(e) => set("theme.accent", e.target.value)} data-testid="accent-custom"
              className="w-24 bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-3 py-2 text-xs font-mono focus:outline-none focus:border-[var(--brand)] transition-colors" />
          </div>
        </div>
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-2">Estilo das fontes</label>
          <select value={content.theme?.font || "padrao"} onChange={(e) => set("theme.font", e.target.value)} data-testid="font-select"
            className="w-full bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors">
            <option value="padrao">Padrão da loja (atual)</option>
            <option value="elegante">Elegante (Playfair Display + Inter)</option>
            <option value="moderna">Moderna (Sora + Work Sans)</option>
            <option value="classica">Clássica (Merriweather + Karla)</option>
          </select>
        </div>
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-2">Cor de fundo do site</label>
          <div className="flex flex-wrap gap-2 items-center" data-testid="bg-swatches">
            {["#FAF8F5", "#FFF7F0", "#F5F0FA", "#F0F7F4", "#FDF3F5", "#1A1816"].map((c) => (
              <button key={c} type="button" onClick={() => set("theme.bg", c)} data-testid={`bg-swatch-${c}`}
                className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${content.theme?.bg === c ? "border-[#1A1816] scale-110" : "border-[#E8E2D9] shadow"}`}
                style={{ backgroundColor: c }} aria-label={`Fundo ${c}`} />
            ))}
            <input value={content.theme?.bg || "#FAF8F5"} onChange={(e) => set("theme.bg", e.target.value)} data-testid="bg-custom"
              className="w-24 bg-white border border-[#E8E2D9] rounded-xl px-3 py-2 text-xs font-mono focus:outline-none focus:border-[var(--brand)] transition-colors" />
          </div>
        </div>
        <p className="text-[11px] text-[#6B645C]">Use "Visualizar" para ver o site com essas alterações antes de publicar.</p>
      </div>

      {/* Seções visíveis e ordem */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-3">
        <h2 className="font-display font-bold text-lg">Seções da Página</h2>
        <p className="text-xs text-[#6B645C]">Ative, desative e mude a ordem das seções da página inicial.</p>
        {content.sections.map((s, idx) => (
          <div key={s.key} className="flex items-center justify-between gap-2 bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5" data-testid={`section-${s.key}`}>
            <span className={`text-sm ${s.visible ? "text-[#1A1816]" : "text-[#A89E94] line-through"}`}>{SECTION_LABELS[s.key] || s.key}</span>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => moveSection(idx, -1)} data-testid={`section-up-${s.key}`} className="p-1.5 rounded-full hover:bg-[#F2EDE4] transition-colors" aria-label="Subir">
                <ArrowUp className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => moveSection(idx, 1)} data-testid={`section-down-${s.key}`} className="p-1.5 rounded-full hover:bg-[#F2EDE4] transition-colors" aria-label="Descer">
                <ArrowDown className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => set(`sections.${idx}.visible`, !s.visible)} data-testid={`section-toggle-${s.key}`} className="p-1.5 rounded-full hover:bg-[#F2EDE4] transition-colors" aria-label="Mostrar/ocultar">
                {s.visible ? <Eye className="w-4 h-4 text-green-700" /> : <EyeOff className="w-4 h-4 text-[#A89E94]" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Aviso promocional */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Aviso no Topo do Site</h2>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={content.announcement.active} onChange={(e) => set("announcement.active", e.target.checked)} data-testid="announcement-toggle" className="w-4 h-4 accent-[var(--brand)]" />
          <span className="text-sm">Exibir faixa de aviso (promoções, feriados, comunicados)</span>
        </label>
        <Field label="Texto do aviso" value={content.announcement.text} onChange={(v) => set("announcement.text", v)} testid="announcement-text" placeholder="Ex: Promoção de Natal — 10% off em toda a loja!" />
      </div>

      {/* Hero */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Chamada Principal (Topo)</h2>
        <Field label="Chamada pequena" value={content.hero.eyebrow} onChange={(v) => set("hero.eyebrow", v)} testid="hero-eyebrow" />
        <Field label="Título — linha 1" value={content.hero.line1} onChange={(v) => set("hero.line1", v)} testid="hero-line1" />
        <Field label="Título — linha 2" value={content.hero.line2} onChange={(v) => set("hero.line2", v)} testid="hero-line2" />
        <Field label="Título — linha 3 (destaque laranja)" value={content.hero.line3} onChange={(v) => set("hero.line3", v)} testid="hero-line3" />
        <Field label="Texto de apoio" value={content.hero.paragraph} onChange={(v) => set("hero.paragraph", v)} multiline testid="hero-paragraph" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Botão principal" value={content.hero.cta_primary} onChange={(v) => set("hero.cta_primary", v)} testid="hero-cta1" />
          <Field label="Botão secundário" value={content.hero.cta_secondary} onChange={(v) => set("hero.cta_secondary", v)} testid="hero-cta2" />
        </div>
        <ImageField label="Imagem principal" value={content.hero.image_url} onChange={(v) => set("hero.image_url", v)} testid="hero-image-upload" />
      </div>

      {/* Marquee */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Faixa Animada</h2>
        <Field label="Frases (uma por linha)" value={content.marquee.join("\n")} onChange={(v) => set("marquee", v.split("\n").filter((l) => l.trim()))} multiline testid="marquee-items" />
      </div>

      {/* Atmosphere */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Destaque Escuro (Como Comprar)</h2>
        <Field label="Título" value={content.atmosphere.title} onChange={(v) => set("atmosphere.title", v)} testid="atmosphere-title" />
        <Field label="Texto" value={content.atmosphere.text} onChange={(v) => set("atmosphere.text", v)} multiline testid="atmosphere-text" />
        <ImageField label="Imagem de fundo" value={content.atmosphere.image_url} onChange={(v) => set("atmosphere.image_url", v)} testid="atmosphere-image-upload" />
      </div>

      {/* Serviços */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Serviços</h2>
        {content.services.map((s, idx) => (
          <div key={idx} className="bg-[var(--site-bg)] border border-[#E8E2D9] rounded-2xl p-4 space-y-3" data-testid={`service-editor-${idx}`}>
            <Field label="Título" value={s.title} onChange={(v) => set(`services.${idx}.title`, v)} testid={`service-title-${idx}`} />
            <Field label="Texto" value={s.text} onChange={(v) => set(`services.${idx}.text`, v)} multiline testid={`service-text-${idx}`} />
            <Field label="Texto do botão" value={s.button_label} onChange={(v) => set(`services.${idx}.button_label`, v)} testid={`service-button-${idx}`} />
          </div>
        ))}
      </div>

      {/* Institucional */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-4">
        <h2 className="font-display font-bold text-lg">Informações da Loja</h2>
        <Field label="Horário de funcionamento" value={content.hours} onChange={(v) => set("hours", v)} testid="content-hours" placeholder="Ex: Seg a Sex 9h às 18h • Sáb 9h às 13h" />
        <Field label="Link do Instagram" value={content.instagram} onChange={(v) => set("instagram", v)} testid="content-instagram" placeholder="https://instagram.com/dracell" />
        <Field label="Título da página (SEO)" value={content.seo.title} onChange={(v) => set("seo.title", v)} testid="seo-title" placeholder="Dra.Cell Assistência Técnica — Celulares e Acessórios" />
        <Field label="Descrição para o Google (SEO)" value={content.seo.description} onChange={(v) => set("seo.description", v)} multiline testid="seo-description" placeholder="Assistência técnica de celulares, venda de celulares e acessórios em Bady Bassitt/SP..." />
      </div>

      {/* Histórico */}
      <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 space-y-3">
        <h2 className="font-display font-bold text-lg inline-flex items-center gap-2"><History className="w-5 h-5 text-[var(--brand)]" /> Versões Anteriores</h2>
        {history.length === 0 ? (
          <p className="text-xs text-[#6B645C]">Nenhuma versão anterior salva ainda.</p>
        ) : (
          history.map((h) => (
            <div key={h.id} className="flex items-center justify-between bg-[var(--site-bg)] border border-[#E8E2D9] rounded-xl px-4 py-2.5" data-testid={`history-${h.id}`}>
              <span className="text-xs text-[#5A534C]">{new Date(h.created_at).toLocaleString("pt-BR")}</span>
              <button type="button" onClick={() => restore(h.id)} data-testid={`restore-${h.id}`}
                className="inline-flex items-center gap-1.5 text-xs text-[#5A534C] hover:text-[var(--brand)] transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> Restaurar
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
